<template>
  <div v-if="loading" class="mx-auto max-w-[1000px] p-4">
    <content-placeholders :rounded="true">
      <content-placeholders-heading :img="true" />
      <content-placeholders-text :lines="3" />
    </content-placeholders>
    <div class="mt-4 px-4">
      <content-placeholders :rounded="true">
        <content-placeholders-text :lines="10" />
      </content-placeholders>
    </div>
  </div>
  <div
    v-else-if="signInLoading"
    class="m-4 mx-auto my-10 max-w-3xl rounded-lg bg-white pt-4"
  >
    <h1 class="text-vgorange mt-5 text-justify text-lg font-semibold md:px-11">
      We are processing your request!
    </h1>
    <p class="text-justify font-semibold md:px-11">
      Please bear with us while we create your consultation request. It may take
      a moment...
    </p>
    <v-progress-linear
      class="mt-5"
      height="15"
      indeterminate
      :color="vgTeal"
    ></v-progress-linear>
  </div>
  <div v-else-if="consultStore.consultState" class="p-3">
    <ProgressBar
      class="mt-10"
      :total-steps="steps.length"
      :current-step="consultStore.consultState.step + 1"
    />
    <keep-alive>
      <component
        :is="steps[consultStore.consultState.step].name"
        :ref="steps[consultStore.consultState.step].name"
        :error="steps[consultStore.consultState.step].error"
        @update="update"
        @submit="submit"
      ></component>
    </keep-alive>
    <div
      class="mx-auto my-0 mb-6 flex w-full max-w-xl flex-col-reverse gap-4 px-5 md:flex-row"
      :class="[
        consultStore.consultState.step === 0
          ? 'justify-end'
          : 'justify-between',
      ]"
    >
      <button
        v-show="consultStore.consultState.step !== 0"
        class="text-vgocean px-3 text-center text-sm font-semibold uppercase underline"
        @click="goBack"
      >
        Back
      </button>
      <v-hover v-slot="{ hover }">
        <vg-btn
          vg-size="large"
          data-cy="consult.steps.btn.next"
          :disabled="buttonLoading"
          @click="goNext"
        >
          <fa-icon
            v-if="buttonLoading"
            :class="[hover ? 'text-ocean' : 'text-white']"
            icon="fa-solid fa-spinner-third fa-spin"
            spin
          />
          <span v-else>{{ shouldShowNextButtonLabel ? 'Next' : 'Skip' }} </span>
        </vg-btn>
      </v-hover>
    </div>
    <div
      v-if="consultStore.consultState && consultStore.consultState.step === 0"
      class="bg-white drop-shadow-lg"
    >
      <v-carousel
        v-model="testimonialsModel"
        :height="uiStore.mobile ? 300 : 200"
        class="mx-auto my-0 px-6 md:max-w-5xl"
        hide-delimiters
        cycle
      >
        <v-carousel-item
          v-for="(testimonial, i) in testimonials"
          :key="testimonial + i"
          class="h-60"
        >
          <div class="flex h-full px-4">
            <div
              class="mx-auto my-0 flex max-w-5xl content-center gap-6 self-center md:px-16"
            >
              <v-avatar size="60" class="md:self-center">
                <v-img :src="testimonial.avatar || '/img/img-quote_avatar.png'">
                </v-img>
              </v-avatar>
              <v-icon
                :color="vgOrange"
                class="-ml-11 self-start md:-mt-10 md:self-center"
                size="large"
                >mdi-comment-quote</v-icon
              >

              <div class="flex flex-col">
                <p class="text-vgsoftpurple text-base italic">
                  {{ testimonials[testimonialsModel].text }}
                </p>
                <p class="text-base italic text-black">
                  {{ testimonials[testimonialsModel].name }}
                </p>
              </div>
            </div>
          </div>
        </v-carousel-item>
      </v-carousel>
    </div>
    <div
      v-if="consultStore.consultState && consultStore.consultState.step === 0"
      class="mx-auto my-16 max-w-5xl px-4"
    >
      <h1 class="pl-3">FAQs</h1>
      <div class="grid md:grid-cols-2 md:gap-20">
        <div>
          <v-expansion-panels>
            <v-expansion-panel
              v-for="(qa, index) in QAS.slice(0, 5)"
              :key="index"
            >
              <v-expansion-panel-title class="px-1 py-0">
                <template #actions>
                  <fa-icon icon="fa-solid fa-chevron-down" class="pr-2" />
                </template>
                <p class="text-vgocean m-0 ml-3 w-full text-left">
                  {{ qa.question }}
                </p>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <p class="text-vgstone text-sm">{{ qa.answer }}</p>
              </v-expansion-panel-text>
              <div class="border-vgsilver-400 mx-3 border"></div>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <div>
          <v-expansion-panels>
            <v-expansion-panel v-for="(qa, index) in QAS.slice(5)" :key="index">
              <v-expansion-panel-title class="px-1 py-0">
                <template #actions>
                  <fa-icon icon="fa-solid fa-chevron-down" class="pr-2" />
                </template>
                <p class="text-vgocean m-0 ml-3 w-full text-left">
                  {{ qa.question }}
                </p>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <p class="text-vgstone text-sm">{{ qa.answer }}</p>
              </v-expansion-panel-text>
              <div class="border-vgsilver-400 mx-3 border"></div>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue';
import { mapStores, storeToRefs } from 'pinia';
import { useCheckoutStore } from '@/stores/checkout';
import { useConsultStore } from '@/stores/consult';
import { useUiStore } from '@/stores/ui';
import { useUserStore } from '@/stores/user';
import WhyChooseUs from '@/components/WhyChooseUs.vue';
import RequirementSelector from '@/components/RequirementSelector.vue';
import PlanDetails from '@/components/PlanDetails.vue';
import MatchingOptimizer from '@/components/MatchingOptimizer.vue';
import ClientCredibility from '@/components/ClientCredibility.vue';
import HowHeard from '@/components/HowHeard.vue';
import ConsultForm from '@/components/ConsultForm.vue';
import ProgressBar from '@/components/ProgressBar.vue';
import tailwind from 'tailwind.config';
import { WEBSITE_URL } from '@/config';
import { getTimezone } from '@/services/userService';
import { createRequestForNewConsult } from '@/services/consultService';
import hubSpotTrackingInit from '@/utils/hubspot-tracking';

import {
  sessionUserHasPermission,
  hasSession,
} from '@/services/clientSessionService';

export default {
  name: 'ConsultWrapper',
  components: {
    WhyChooseUs,
    RequirementSelector,
    PlanDetails,
    MatchingOptimizer,
    HowHeard,
    ConsultForm,
    ProgressBar,
    ClientCredibility,
  },
  data() {
    const { user, client } = storeToRefs(useUserStore());

    return {
      user,
      client,
      signInLoading: false,
      loading: true,
      buttonLoading: false,
      steps: [
        {
          name: 'WhyChooseUs',
          Qkeys: ['whyChooseUs'],
          error: null,
          removeIfLoggedIn: true,
          component: 'WhyChooseUs',
        },
        {
          name: 'RequirementSelector',
          Qkeys: ['RequirementSelector'],
          error: null,
          component: 'RequirementSelector',
          checkKeywords: true,
          showSkipLabel: true,
        },
        {
          name: 'ClientCredibility',
          error: null,
          component: 'ClientCredibility',
        },
        {
          name: 'PlanDetails',
          Qkeys: ['duration', 'urgency', 'monthlyBudget'],
          error: null,
          component: 'PlanDetails',
        },
        {
          name: 'MatchingOptimizer',
          Qkeys: ['matchingInfo'],
          error: null,
          component: 'MatchingOptimizer',
          showSkipLabel: true,
        },
        {
          name: 'HowHeard',
          Qkeys: ['howHeard', 'socialMission'],
          error: null,
          removeIfLoggedIn: true,
          component: 'HowHeard',
        },
        {
          name: 'ConsultForm',
          keys: ['ConsultForm'],
          error: null,
          component: 'ConsultForm',
          removeIfLoggedIn: true,
        },
      ],
      testimonials: [
        {
          text: `They make it easy to get started and get who you need. My VA is a treasure and has put my research months ahead.`,
          name: `Sumner D, Founder & CEO, Tradesflow`,
          avatar: '/img/clients/Sumner D.jpg',
        },
        {
          text: `I've had exceptional support for growing my business. I highly recommend Virtual Gurus for startups and consulting organizations.`,
          name: `Dr. Lynn P, Founder, Deep Vivid`,
          avatar: '/img/clients/Lynn P.jpg',
        },
        {
          text: `We’re thrilled to support their mission to provide opportunities for diverse communities. It’s not just a placement approach; it’s a people partnership.`,
          name: `Carey H, Founder & Partner, 321 Growth Academy`,
          avatar: '/img/clients/Carey H.jpg',
        },
      ],
      QAS: [
        {
          question: 'Do I need to pick my own VA?',
          answer: `We take the load off of you by doing the matching for you, with our large pool of talent we don’t expect clients to dig through instead our matching team will present curated choices to find the best fit.

`,
        },
        {
          question: 'Do I always have the same VA?',
          answer: `Yes, barring life events or other unforeseen circumstances the VA or VAswho are matched with you will be working with you for your time with us.

`,
        },
        {
          question: 'Do I need to train my VA?',
          answer: `Yes, while your VA will have the right skills you will need to provide training and guidance with your unique systems and needs. Most clients see full productivity from their VA within 4-6 weeks.

        `,
        },
        {
          question: 'Do I need to provide software or hardware?',
          answer: `All VA’s already have their own computer. Most VA’s already have a basic software (office etc) suite however specific software needs or seats would need to be provided by yourself both for security and logistical purposes.

        `,
        },
        {
          question: 'How do I know my time is being used?',
          answer: `Our custom software solution, Talentplace, provides a minute by minute breakdown of the work being done as well as weekly reports.

        `,
        },
        {
          question: 'Do my hours roll over?',
          answer: `No, to ensure that we are maintaining continuity of service for you and quality of life for our VAs you need to use your hours within the month, similar to a cell phone plan.

          `,
        },
        {
          question: 'Does it cost more to have more than one VA?',
          answer: `No, your cost is for our service which can be built to meet your needs, one VA or more as is best for you.

`,
        },
        {
          question: 'When do I pay?',
          answer: `You will pay for your first month as soon as you sign up so our service can start matching you with the right VA.`,
        },
        {
          question: 'Do you have a referral program?',
          answer: `Yes, we love when our client’s recommend us to others, and to show our appreciation we provide incentives to our current clients of a discount on the next monthly bill or additional free hours. `,
        },
      ],
      testimonialsModel: 0,
      vgOrange: tailwind.theme.extend.colors.vgorange[500],
      vgTeal: tailwind.theme.extend.colors.vgteal[500],
    };
  },
  computed: {
    ...mapStores(useUiStore, useConsultStore, useCheckoutStore),
    consultForm() {
      return {
        company: this.user?.company,
        full_name: this.user?.full_name,
        known_as: this.user?.known_as,
        email: this.user?.email,
        phone_number: this.user?.phone_number,
        website: this.client?.client.website,
        industry: this.client?.client.industry,
        size: this.client?.client.size,
      };
    },
    shouldShowNextButtonLabel() {
      if (
        this.steps[this.consultStore.consultState.step].name ===
        'RequirementSelector'
      ) {
        return this.checkoutStore.flattedEnteredKeywords.length > 0;
      }
      if (
        this.steps[this.consultStore.consultState.step].name ===
        'MatchingOptimizer'
      ) {
        return (
          this.consultStore.consultState.questions.find(
            (question) => question.key === 'matchingInfo'
          )?.A.length > 0
        );
      }

      return true;
    },
  },
  async created() {
    if (hasSession()) {
      if (sessionUserHasPermission('virtualassistant')) {
        return this.$router.push('/va/home');
      }
      if (sessionUserHasPermission('admin')) {
        return this.$router.push('/');
      }
    }

    this.consultStore.initialSetUp();
    await hubSpotTrackingInit().catch(() => {});

    if (!this.consultStore.consultState?.step) {
      this.consultStore.updateConsult({ step: 0 });
    }
    if (this.user) {
      this.steps = this.steps.filter((step) => !step.removeIfLoggedIn);

      if (this.consultStore.consultState.questions) {
        let consultStateAfterLogin = { ...this.consultStore.consultState };

        consultStateAfterLogin.questions = this.consultStore.consultState?.questions?.filter(
          (question) =>
            this.steps.find(
              (step) =>
                step?.Qkeys?.includes(question.key) && !step.removeIfLoggedIn
            )
        );

        consultStateAfterLogin.step = consultStateAfterLogin.questions
          ? this.steps.filter((step) =>
              step?.Qkeys?.some((key) =>
                consultStateAfterLogin.questions.find(
                  (question) => question.key === key
                )
              )
            )?.length
          : 0;

        this.consultStore.updateConsult({
          ...consultStateAfterLogin,
        });
      }

      if (this.consultStore.consultState.stage === 'postSignIn') {
        this.loading = false;
        await this.submit(this.consultForm);
      }
    }

    this.loading = false;
  },
  methods: {
    goBack() {
      if (this.consultStore.consultState.step !== 0) {
        this.consultStore.updateConsult({
          ...this.consultStore.consultState,
          step: this.consultStore.consultState.step - 1,
        });
      }
    },
    async goNext() {
      if (this.loading) {
        return;
      }

      if (
        this.consultStore.consultState?.questions?.find(
          (question) =>
            question.key === 'whyChooseUs' &&
            question?.A !==
              `My company or I need the help of one or more remote assistants`
        )
      ) {
        const answer = this.consultStore.consultState?.questions?.find(
          (question) => question.key === 'whyChooseUs'
        ).A;

        this.consultStore.removeConsult();

        if (answer === `I'd like to discuss a partnership opportunity`) {
          this.$gtm?.trackEvent({
            event: 'DL - Consult - Need partnership',
          });
          window.open(`${WEBSITE_URL}/company/contact?inquiry=partner`);
        } else if (answer === `I'd like to become a remote assistant`) {
          this.$gtm?.trackEvent({
            event: 'DL - Consult - Become remote assistant ',
          });
          this.$router.push(`/va/apply`);
        }
      } else if (
        this.steps[this.consultStore.consultState.step]?.name ===
        'MatchingOptimizer'
      ) {
        if (this.user && this.client) {
          await this.submit(this.consultForm);
        }

        this.logConsultFlowEvent(
          this.steps[this.consultStore.consultState.step]?.name
        );
        this.consultStore.updateConsult({
          ...this.consultStore.consultState,
          step: this.consultStore.consultState.step + 1,
        });
        this.$gtm?.trackEvent({
          event: 'DL - Consult - Matching info',
          value: this.consultStore.consultState.questions.find(
            (question) => question.key === 'matchingInfo'
          ).A,
        });
      } else if (
        this.steps[this.consultStore.consultState.step]?.name ===
        'ClientCredibility'
      ) {
        this.$gtm?.trackEvent({
          event: 'DL - Consult - Credibility',
        });
        this.consultStore.updateConsult({
          ...this.consultStore.consultState,
          step: this.consultStore.consultState.step + 1,
        });
      } else if (
        (this.steps[this.consultStore.consultState.step]?.Qkeys?.every((key) =>
          this.consultStore.consultState?.questions?.find(
            (question) => question.key === key
          )
        ) &&
          this.consultStore.consultState.step !== this.steps.length - 1) ||
        this.steps[this.consultStore.consultState.step]?.checkKeywords
      ) {
        if (
          this.steps[this.consultStore.consultState.step]?.name ===
          'WhyChooseUs'
        ) {
          this.$gtm?.trackEvent({
            event: 'DL - Consult - Need remote assistant',
          });
        } else if (
          this.steps[this.consultStore.consultState.step]?.name === 'HowHeard'
        ) {
          this.$gtm?.trackEvent({
            event: 'DL - Consult - Social mission awareness',
            value: this.consultStore.consultState.questions.find(
              (question) => question.key === 'socialMission'
            ).A,
          });
        }

        this.logConsultFlowEvent(
          this.steps[this.consultStore.consultState.step]?.name
        );
        this.consultStore.updateConsult({
          ...this.consultStore.consultState,
          step: this.consultStore.consultState.step + 1,
        });
      } else if (
        this.consultStore.consultState.step ===
        this.steps.length - 1
      ) {
        nextTick(() => {
          this.$refs.ConsultForm.submit();
        });
        this.$gtm?.trackEvent({
          event: 'DL - Consult - Registration submission',
        });
      } else {
        let newArr = [...this.steps];

        newArr[this.consultStore.consultState.step] = {
          ...newArr[this.consultStore.consultState.step],
          error: true,
        };
        this.steps = [...newArr];
      }
    },
    update(newData) {
      if (!newData.key) {
        return;
      }
      let consultStateToUpdate = {
        ...this.consultStore.consultState,
        questions: [...(this.consultStore.consultState?.questions || [])],
      };
      const indexOfDataToUpdate = consultStateToUpdate.questions?.findIndex(
        (item) => item.key === newData.key
      );
      if (indexOfDataToUpdate === -1) {
        consultStateToUpdate.questions.push(newData);
      } else {
        consultStateToUpdate.questions[indexOfDataToUpdate] = newData;
      }

      let newArr = [...this.steps];

      newArr[consultStateToUpdate.step] = {
        ...newArr[consultStateToUpdate.step],
        error: null,
      };
      this.steps = [...newArr];
      this.consultStore.updateConsult(consultStateToUpdate);
    },
    setLoading(payload) {
      this.buttonLoading = payload;
    },

    async submit(form) {
      this.signInLoading = true;

      //check email exists
      await this.checkoutStore.searchRoles();

      try {
        const { timezone, timezone_offset } = this.user
          ? {}
          : await getTimezone(form.latitude, form.longitude);

        const utm = localStorage.getItem('vg.utm');
        const rids = this.checkoutStore.matchingCriteria?.keywords?.roles?.map(
          (role) =>
            this.checkoutStore.roles?.find(
              (loadedRole) => loadedRole.value === role
            )?.id
        );
        const monthlyBudget =
          this.consultStore.consultState.questions?.find(
            (question) => question.key === 'monthlyBudget'
          )?.A || '';
        let meeting_type = 'smb2';
        let contactType = 'SMB';

        if (monthlyBudget === 'Less than $350') {
          meeting_type = 'smb2';
          contactType = 'SMB';
        } else if (
          form.size === 'large' ||
          monthlyBudget === '$5,000 to $24,999' ||
          monthlyBudget === 'More than $25,000'
        ) {
          meeting_type = 'entr';
          contactType = 'Enterprise';
        } else if (
          (form.size === 'micro' ||
            form.size === 'small' ||
            form.size === 'small-med') &&
          (rids?.includes(1) || rids?.includes(9))
        ) {
          meeting_type = 'smb1';
          contactType = 'SMB';
        }

        const consultRequest = await createRequestForNewConsult({
          ...form,
          rids,
          tids: this.checkoutStore.matchingCriteria.tids,
          sids: this.checkoutStore.matchingCriteria.sids,
          timezone,
          timezone_offset,
          ...(utm ? { utm: JSON.parse(utm) } : {}),
          questions: this.consultStore.consultState.questions,
          matching_criteria: this.checkoutStore.matchingCriteria,
          contactType,
          meetingType: meeting_type,
        });

        window._hsq?.push([
          'identify',
          {
            email: consultRequest.business_email ?? this.user?.email,
          },
        ]);
        window._hsq?.push(['trackPageView']);

        this.consultStore.updateConsult({
          ...this.consultStore.consultState,
          project_uuid: consultRequest.project_uuid,
          full_name: consultRequest.full_name,
          business_email: consultRequest.business_email,
          owner_id: consultRequest.owner_id,
        });

        return this.$router.push({
          path: `/client/meeting/${meeting_type}`,
          query: {
            project_uuid: consultRequest.project_uuid,
            full_name: consultRequest.full_name,
            business_email: consultRequest.business_email,
            owner_id: consultRequest.owner_id,
          },
        });
      } catch (error) {
        this.setLoading(false);

        this.$root.$snackbar.error(
          error?.data?.message ??
            `Unable to submit your request at this time, please try again later.`
        );
      } finally {
        this.signInLoading = false;
        this.consultStore.updateConsult({
          ...this.consultStore.consultState,
          stage: '',
        });
      }
    },
    logConsultFlowEvent(step) {
      if (!step) {
        return;
      }

      this.$gtm?.trackEvent({
        event: `VG - Consult Flow - ${step}`,
      });
    },
  },
};
</script>

<style scoped>
.fa-spin {
  animation-duration: 0.9s;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

* :deep(.v-theme--light.v-expansion-panels .v-expansion-panel) {
  background-color: rgba(0, 0, 0, 0);
}

* :deep(.v-text-field > .v-input__control fieldset) {
  padding-left: 15px;
}

* :deep(.v-window__next) {
  right: 0;
}
* :deep(.v-expansion-panel .v-expansion-panel__shadow),
* :deep(.v-expansion-panels--variant-default > .v-expansion-panel::after) {
  @apply hidden;
}
* :deep(.v-expansion-panel-title__overlay) {
  @apply shadow-none opacity-0 hidden;
}
*
  :deep(.v-expansion-panel--active
    > .v-expansion-panel-title--active
    .v-expansion-panel-title__icon:not(.v-expansion-panel-title__icon--disable-rotate)) {
  -webkit-transform: rotate(-180deg);
  transform: rotate(-180deg);
}
*
  :deep(.v-expansion-panel
    > .v-expansion-panel-title
    .v-expansion-panel-title__icon:not(.v-expansion-panel-title__icon--disable-rotate)) {
  @apply transition-transform;
}
</style>
